// Hover tooltips for toolbar buttons: the name, its shortcut and a short
// explanation. Elements opt in with tip(); one floating element serves the
// whole editor. Native `title` tooltips appear late and plain, so buttons
// with a tip don't set one.

import { useEffect, useLayoutEffect, useRef, useState } from 'react';

export type TipSide = 'bottom' | 'top' | 'right';

/** Props that give an element a hover tooltip. */
export function tip(label: string, opts: { keys?: string; hint?: string; side?: TipSide } = {}): Record<string, string | undefined> {
  return { 'data-tip': label, 'data-tip-keys': opts.keys, 'data-tip-hint': opts.hint, 'data-tip-side': opts.side };
}

interface Shown {
  el: HTMLElement;
  label: string;
  keys?: string;
  hint?: string;
  side: TipSide;
}

const SHOW_DELAY = 350;
/** Moving on to another button soon after a tooltip closes shows the next one at once. */
const WARM_MS = 500;
const GAP = 6;
const MARGIN = 8;

function read(el: HTMLElement): Shown {
  const d = el.dataset;
  return { el, label: d.tip ?? '', keys: d.tipKeys || undefined, hint: d.tipHint || undefined, side: (d.tipSide as TipSide) || 'bottom' };
}

export function Tooltips() {
  const [shown, setShown] = useState<Shown | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    let current: HTMLElement | null = null; // element under the pointer (or focused)
    let open = false;
    let warmUntil = 0;
    let suppressed: HTMLElement | null = null; // clicked: no tooltip until the pointer leaves it

    const show = (el: HTMLElement) => {
      if (!el.isConnected || document.body.classList.contains('note-dragging')) return;
      open = true;
      setShown(read(el));
    };
    const close = () => {
      clearTimeout(timer);
      if (open) warmUntil = performance.now() + WARM_MS;
      open = false;
      setShown(null);
    };
    const target = (el: HTMLElement | null) => {
      if (el === current) return;
      clearTimeout(timer);
      current = el;
      if (el !== suppressed) suppressed = null;
      if (!el || el === suppressed) return close();
      if (open || performance.now() < warmUntil) show(el);
      else timer = setTimeout(() => show(el), SHOW_DELAY);
    };
    const tipEl = (t: EventTarget | null) => (t instanceof Element ? t.closest<HTMLElement>('[data-tip]') : null);

    const over = (e: PointerEvent) => {
      if (e.pointerType === 'touch') return;
      target(tipEl(e.target));
    };
    const out = (e: PointerEvent) => {
      if (!e.relatedTarget) target(null); // left the window
    };
    const down = () => {
      suppressed = current;
      close();
    };
    const focusIn = (e: FocusEvent) => {
      const el = tipEl(e.target);
      if (el?.matches(':focus-visible')) target(el);
    };
    const focusOut = () => {
      if (current && !current.matches(':hover')) target(null);
    };

    document.addEventListener('pointerover', over, true);
    document.addEventListener('pointerout', out, true);
    document.addEventListener('pointerdown', down, true);
    document.addEventListener('focusin', focusIn, true);
    document.addEventListener('focusout', focusOut, true);
    document.addEventListener('keydown', close, true);
    window.addEventListener('scroll', close, true);
    window.addEventListener('blur', close);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('pointerover', over, true);
      document.removeEventListener('pointerout', out, true);
      document.removeEventListener('pointerdown', down, true);
      document.removeEventListener('focusin', focusIn, true);
      document.removeEventListener('focusout', focusOut, true);
      document.removeEventListener('keydown', close, true);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('blur', close);
    };
  }, []);

  // Place next to the element, flipped or clamped to stay on screen.
  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!shown || !box) return;
    const r = shown.el.getBoundingClientRect();
    const w = box.offsetWidth;
    const h = box.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let side = shown.side;
    if (side === 'bottom' && r.bottom + GAP + h > vh - MARGIN) side = 'top';
    else if (side === 'top' && r.top - GAP - h < MARGIN) side = 'bottom';
    else if (side === 'right' && r.right + GAP + w > vw - MARGIN) side = 'bottom';
    let left: number;
    let top: number;
    if (side === 'right') {
      left = r.right + GAP;
      top = r.top + r.height / 2 - h / 2;
    } else {
      left = r.left + r.width / 2 - w / 2;
      top = side === 'bottom' ? r.bottom + GAP : r.top - GAP - h;
    }
    box.style.left = `${Math.round(Math.max(MARGIN, Math.min(vw - MARGIN - w, left)))}px`;
    box.style.top = `${Math.round(Math.max(MARGIN, Math.min(vh - MARGIN - h, top)))}px`;
    box.dataset.side = side;
  }, [shown]);

  if (!shown) return null;
  return (
    <div className="tooltip" role="tooltip" ref={boxRef}>
      <div className="tooltip-title">
        <span>{shown.label}</span>
        {shown.keys && <kbd>{shown.keys}</kbd>}
      </div>
      {shown.hint && <div className="tooltip-hint">{shown.hint}</div>}
    </div>
  );
}
